import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectorRef,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RegsApiService } from '../../data/regs-api.service';
import { RegsFollowService } from '../../data/regs-follow.service';
import { RegsChoiceService } from '../../data/regs-choice.service';
import {
  RegsIndex,
  SpeciesSummary,
  Species,
  Sector,
  Waters,
  Lookalike,
  ChangeEvent,
  regsStateName,
  regsPair,
  regsConfirmText,
} from '../../domain/regs-models';
import {
  sizeText,
  splitBag,
  mdLabel,
  parseAsk,
} from '../../domain/regs-answer';
import { RegsTwoWayComponent } from '../widgets/regs-two-way.component';
import { KeepItAnswerPageComponent } from '../keep-it-answer-page/keep-it-answer-page.component';
import { KeepItWatersPickerComponent } from '../keep-it-waters-picker/keep-it-waters-picker.component';
import { KeepItIdPageComponent } from '../keep-it-id-page/keep-it-id-page.component';
import { KeepItChangesPageComponent } from '../keep-it-changes-page/keep-it-changes-page.component';

export interface NavEntry {
  view: 'list' | 'answer' | 'watersPicker' | 'idPage' | 'changesPage';
  speciesId?: string;
  species?: Species;
  lookalike?: Lookalike;
  sector?: Sector;
  waters?: Waters;
  question?: string;
  askedInches?: number;
  intro?: string;
}

@Component({
  selector: 'app-keep-it-sheet',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RegsTwoWayComponent,
    KeepItAnswerPageComponent,
    KeepItWatersPickerComponent,
    KeepItIdPageComponent,
    KeepItChangesPageComponent,
  ],
  template: `
    <div class="keep-it-backdrop" (click)="onBackdropClick($event)">
      <div class="keep-it-sheet" (click)="$event.stopPropagation()">
        <!-- Grabber for mobile handle -->
        <div class="sheet-grabber">
          <div class="grabber-bar"></div>
        </div>

        <!-- STACK SCREEN: LIST VIEW -->
        <ng-container *ngIf="currentEntry.view === 'list'">
          <!-- Sheet Header -->
          <div class="sheet-header">
            <div class="header-avatar">
              <img src="assets/skipper/skipper_avatar.png" alt="Skipper" (error)="avatarFallback($event)" />
            </div>
            <div class="header-titles">
              <h2 class="title">Can I keep it?</h2>
              <div class="subtitle">{{ headerSubtitle }}</div>
            </div>
            <div class="header-actions">
              <button type="button" class="round-btn close-btn" (click)="close.emit()" title="Close">
                <span class="material-icons">close</span>
              </button>
            </div>
          </div>

          <!-- Main Scrollable Content -->
          <div class="body-scroll" *ngIf="index; else loadingOrError">
            <!-- Skipper Greeting / Ask reply -->
            <div class="skipper-says-card" *ngIf="skipperGreeting">
              <div class="skipper-avatar">
                <img src="assets/skipper/skipper_avatar.png" alt="Skipper" (error)="avatarFallback($event)" />
              </div>
              <div class="skipper-content">
                <div class="skipper-name">Skipper</div>
                <div class="skipper-text">{{ skipperGreeting }}</div>
              </div>
            </div>

            <!-- Location Note (WhereNote) -->
            <div *ngIf="!isFollowingTab && choice.here()" class="location-banner">
              <div class="loc-icon-wrap">
                <span class="material-icons">{{ choice.here()!.nearBoundary ? 'info' : 'near_me' }}</span>
              </div>
              <div class="loc-content">
                <div class="loc-text">{{ whereNoteText }}</div>
                <div class="loc-actions">
                  <button *ngIf="choice.showingElsewhere()" type="button" class="btn-link" (click)="choice.useHere()">
                    Back to {{ choice.hereWaters()?.label }} rules
                  </button>
                  <ng-container *ngIf="!choice.showingElsewhere() && choice.hereWaters()">
                    <button *ngIf="pairedWaters" type="button" class="btn-link" (click)="choice.pick(pairedWaters)">
                      Show {{ pairedWaters.label }} instead
                    </button>
                  </ng-container>
                  <button type="button" class="btn-change-waters" (click)="openWatersPicker()">
                    Change state
                  </button>
                </div>
              </div>
            </div>

            <!-- Unknown Waters Note -->
            <div *ngIf="!isFollowingTab && !choice.here() && choice.problem()" class="location-banner unknown-banner">
              <div class="loc-icon-wrap">
                <span class="material-icons">info</span>
              </div>
              <div class="loc-content">
                <div class="loc-text">{{ unknownNoteText }}</div>
                <div class="loc-actions">
                  <button type="button" class="btn-link" (click)="choice.locate()">
                    Check again
                  </button>
                  <button type="button" class="btn-change-waters" (click)="openWatersPicker()">
                    {{ choice.chosenByHand() ? 'Change state' : 'Choose a state' }}
                  </button>
                </div>
              </div>
            </div>

            <!-- No Position Note -->
            <div *ngIf="!isFollowingTab && choice.noPosition()" class="no-position-banner">
              <div class="sub-text">
                Showing {{ choice.waters().label }} rules. Connect your boat or turn on location and I’ll match the waters you’re in.
              </div>
              <button type="button" class="btn-change-waters" (click)="openWatersPicker()">
                Change state
              </button>
            </div>

            <!-- Two-Way Tab Bar: All fish / Following (if following enabled or opened on Following) -->
            <div *ngIf="initialFollowing || followsCount > 0" class="tab-two-way">
              <app-regs-two-way
                [values]="[false, true]"
                [labels]="['All fish', followingTabLabel]"
                [value]="isFollowingTab"
                (valueChange)="setFollowingTab($event)"
              ></app-regs-two-way>
            </div>

            <!-- ALL FISH TAB CONTENT -->
            <ng-container *ngIf="!isFollowingTab">
              <!-- Sector Toggle (Recreational / Commercial) -->
              <div class="sector-toggle-wrap">
                <app-regs-two-way
                  [values]="['recreational', 'commercial']"
                  [labels]="['Recreational', 'Commercial']"
                  [value]="choice.sector()"
                  (valueChange)="choice.set({ sector: $any($event) })"
                ></app-regs-two-way>
              </div>

              <!-- Species List Grouped by Family/Group -->
              <div class="species-list-container" *ngIf="choice.canShowRules()">
                <div *ngFor="let g of index.groups" class="group-section">
                  <ng-container *ngIf="getSpeciesInGroup(g).length > 0">
                    <div class="group-header">{{ g }}</div>
                    <div class="species-rows">
                      <div
                        *ngFor="let sp of getSpeciesInGroup(g)"
                        class="species-card"
                        (click)="openAnswer(sp.id)"
                      >
                        <div class="fish-icon-tile">
                          <span class="material-icons">set_meal</span>
                        </div>
                        <div class="species-info">
                          <div class="species-name">{{ sp.short }}</div>
                          <div class="species-sub" *ngIf="getSpeciesSub(sp)">{{ getSpeciesSub(sp) }}</div>
                        </div>
                        <div class="status-chip" [ngClass]="getChipClass(sp)">
                          <span *ngIf="getChipBag(sp)" class="chip-big">{{ getChipBag(sp) }}</span>
                          <span class="chip-label">{{ getChipLabel(sp) }}</span>
                        </div>
                      </div>
                    </div>
                  </ng-container>
                </div>

                <!-- Legal Disclaimer -->
                <div class="disclaimer-text">
                  {{ index.disclaimer || confirmText }}
                </div>
              </div>
            </ng-container>

            <!-- FOLLOWING TAB CONTENT -->
            <ng-container *ngIf="isFollowingTab">
              <!-- Followed Species List -->
              <div class="following-list" *ngIf="followedItems.length > 0; else noFollows">
                <div *ngFor="let item of followedItems" class="followed-card" (click)="openAnswer(item.sp.id, item.sector)">
                  <div class="fish-icon-tile">
                    <span class="material-icons">set_meal</span>
                  </div>
                  <div class="followed-info">
                    <div class="followed-title">{{ item.sp.short }}</div>
                    <div class="followed-sub">{{ sectorLabel(item.sector) }} · {{ choice.waters().label }}</div>
                    <div class="followed-status" [class.is-new]="item.isNew">
                      {{ item.statusText }}
                    </div>
                  </div>
                  <div *ngIf="item.isNew" class="new-dot"></div>
                  <button
                    type="button"
                    class="btn-unfollow"
                    (click)="confirmUnfollow($event, item.sp.id, item.sector, item.sp.short)"
                    title="Stop following"
                  >
                    <span class="material-icons">close</span>
                  </button>
                </div>

                <!-- Recent Changes Card -->
                <div class="card-box changes-timeline-card" *ngIf="followsService.recent.length > 0 || upcomingEvents.length > 0">
                  <div class="section-label">Recent changes</div>
                  <div class="timeline-items">
                    <div *ngFor="let e of followsService.recent.slice(0, 12)" class="timeline-row">
                      <div class="timeline-dot" [class.dot-new]="followsService.isNew(e)"></div>
                      <div class="timeline-body">
                        <div class="timeline-time">{{ formatTimeAgo(e.at) }}</div>
                        <div class="timeline-text">
                          <span class="timeline-fish">{{ e.short }}: </span>
                          <span>{{ e.text }}</span>
                        </div>
                      </div>
                    </div>

                    <div *ngFor="let up of upcomingEvents" class="timeline-row">
                      <div class="timeline-dot dot-upcoming"></div>
                      <div class="timeline-body">
                        <div class="timeline-time">{{ up.when }}</div>
                        <div class="timeline-text">
                          <span class="timeline-fish">{{ up.fish }}: </span>
                          <span>{{ up.what }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <ng-template #noFollows>
                <div class="no-follows-empty">
                  <span class="material-icons empty-icon">notifications_none</span>
                  <div class="empty-title">No fish followed yet</div>
                  <div class="empty-desc">
                    Tap Follow on any fish to keep watch on its size limits, seasons, and quota alerts.
                  </div>
                </div>
              </ng-template>
            </ng-container>
          </div>

          <!-- Bottom Ask Bar -->
          <div class="ask-bar-container">
            <form (ngSubmit)="submitAsk()" class="ask-form">
              <input
                type="text"
                [(ngModel)]="askText"
                name="askSheetInput"
                placeholder="Ask about a fish or size…"
                class="ask-input"
                autocomplete="off"
              />
              <button type="submit" class="ask-submit-btn" [disabled]="!askText.trim()" title="Send">
                <span class="material-icons">arrow_upward</span>
              </button>
            </form>
          </div>
        </ng-container>

        <!-- STACK SCREEN: ANSWER PAGE -->
        <ng-container *ngIf="currentEntry.view === 'answer'">
          <app-keep-it-answer-page
            [speciesId]="currentEntry.speciesId!"
            [sector]="currentEntry.sector || choice.sector()"
            [waters]="currentEntry.waters || choice.waters()"
            [question]="currentEntry.question"
            [askedInches]="currentEntry.askedInches"
            [intro]="currentEntry.intro"
            (back)="popStack()"
            (close)="close.emit()"
            (openRelated)="pushStackAnswer($event)"
            (openIdPage)="pushStackId($event.species, $event.lookalike)"
            (openChangesPage)="pushStackChanges($event.species, $event.sector)"
          ></app-keep-it-answer-page>
        </ng-container>

        <!-- STACK SCREEN: WATERS PICKER PAGE -->
        <ng-container *ngIf="currentEntry.view === 'watersPicker'">
          <app-keep-it-waters-picker
            [idx]="index!"
            [currentWaters]="choice.waters()"
            [hereWaters]="choice.hereWaters()"
            (select)="onWatersSelected($event)"
            (back)="popStack()"
            (close)="popStack()"
          ></app-keep-it-waters-picker>
        </ng-container>

        <!-- STACK SCREEN: ID COMPARISON PAGE -->
        <ng-container *ngIf="currentEntry.view === 'idPage'">
          <app-keep-it-id-page
            [species]="currentEntry.species!"
            [lookalike]="currentEntry.lookalike!"
            (back)="popStack()"
            (close)="close.emit()"
          ></app-keep-it-id-page>
        </ng-container>

        <!-- STACK SCREEN: CHANGES HISTORY PAGE -->
        <ng-container *ngIf="currentEntry.view === 'changesPage'">
          <app-keep-it-changes-page
            [species]="currentEntry.species!"
            [sector]="currentEntry.sector || choice.sector()"
            (back)="popStack()"
            (close)="close.emit()"
          ></app-keep-it-changes-page>
        </ng-container>

        <!-- Loading / Error View -->
        <ng-template #loadingOrError>
          <div class="center-loading">
            <div *ngIf="error" class="error-msg">
              <p>{{ error }}</p>
              <button type="button" class="btn-retry" (click)="loadIndex()">Try again</button>
            </div>
            <div *ngIf="!error" class="spinner-view">
              <span class="material-icons spin">refresh</span>
              <span>Loading regulations...</span>
            </div>
          </div>
        </ng-template>
      </div>
    </div>
  `,
  styles: [`
    .keep-it-backdrop {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.32);
      backdrop-filter: blur(2px);
      z-index: 10000;
      display: flex;
      justify-content: flex-end;
      align-items: stretch;
      animation: keepItFade 200ms ease-out both;
    }

    @keyframes keepItFade {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .keep-it-sheet {
      position: relative;
      width: 440px;
      max-width: 100vw;
      height: 100%;
      height: 100dvh;
      max-height: 100vh;
      background: #ffffff;
      border-radius: 20px 0 0 20px;
      box-shadow: -4px 0 24px rgba(0, 0, 0, 0.14);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      outline: none;
      animation: keepItSlideIn 260ms cubic-bezier(0.215, 0.61, 0.355, 1) both;
    }

    @media (max-width: 639px) {
      .keep-it-sheet {
        width: 100vw;
        border-radius: 0;
      }
    }

    @keyframes keepItSlideIn {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    .sheet-grabber {
      display: none;
    }

    .sheet-header {
      display: flex;
      align-items: center;
      padding: 10px 16px 12px;
      border-bottom: 1px solid #E5E8EB;
      background: #ffffff;
      z-index: 2;
    }

    .header-avatar img {
      width: 38px;
      height: 38px;
      border-radius: 50%;
      object-fit: cover;
    }

    .header-titles {
      flex: 1;
      margin: 0 12px;
    }

    .header-titles .title {
      font-size: 18px;
      font-weight: 700;
      margin: 0;
      color: #0F1C2E;
    }

    .header-titles .subtitle {
      font-size: 13px;
      color: #5A6E85;
      margin-top: 1px;
    }

    .round-btn {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: #F0F4F8;
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #0F1C2E;
      cursor: pointer;
      transition: background 0.15s ease;
    }

    .round-btn:hover {
      background: #E2E8F0;
    }

    .body-scroll {
      flex: 1;
      overflow-y: auto;
      padding: 14px 16px 24px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .skipper-says-card {
      display: flex;
      gap: 12px;
      padding: 12px 14px;
      background: #F7F9FC;
      border: 1px solid #E8EEF5;
      border-radius: 16px;
    }

    .skipper-avatar img {
      width: 34px;
      height: 34px;
      border-radius: 50%;
    }

    .skipper-content {
      flex: 1;
    }

    .skipper-name {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      color: #007AFF;
      margin-bottom: 2px;
    }

    .skipper-text {
      font-size: 14px;
      color: #0F1C2E;
      line-height: 1.4;
    }

    .location-banner {
      display: flex;
      gap: 10px;
      padding: 12px 14px;
      background: #F0F4F8;
      border-radius: 14px;
    }

    .loc-icon-wrap {
      color: #007AFF;
      font-size: 18px;
      margin-top: 2px;
    }

    .loc-content {
      flex: 1;
    }

    .loc-text {
      font-size: 14px;
      color: #0F1C2E;
      line-height: 1.4;
    }

    .loc-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      align-items: center;
      margin-top: 8px;
    }

    .btn-link {
      background: transparent;
      border: none;
      color: #007AFF;
      font-size: 13.5px;
      font-weight: 600;
      padding: 0;
      cursor: pointer;
    }

    .btn-change-waters {
      background: #ffffff;
      border: 1px solid #D2DFEE;
      color: #007AFF;
      font-size: 13px;
      font-weight: 600;
      padding: 5px 12px;
      border-radius: 14px;
      cursor: pointer;
    }

    .no-position-banner {
      padding: 12px 14px;
      background: #F0F4F8;
      border-radius: 14px;
      font-size: 13.5px;
      color: #5A6E85;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .tab-two-way {
      margin-bottom: 2px;
    }

    .sector-toggle-wrap {
      margin-bottom: 6px;
    }

    .group-section {
      margin-top: 14px;
    }

    .group-header {
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.6px;
      color: #5A6E85;
      text-transform: uppercase;
      margin-bottom: 8px;
      padding: 0 4px;
    }

    .species-rows {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .species-card {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 14px;
      background: #ffffff;
      border: 1px solid #E5EAF0;
      border-radius: 16px;
      cursor: pointer;
      transition: all 0.15s ease;
    }

    .species-card:hover {
      background: #F8FAFD;
      border-color: #D2DFEE;
    }

    .fish-icon-tile {
      width: 36px;
      height: 36px;
      border-radius: 10px;
      background: #F0F4F8;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #007AFF;
    }

    .fish-icon-tile .material-icons {
      font-size: 20px;
    }

    .species-info {
      flex: 1;
      overflow: hidden;
    }

    .species-name {
      font-size: 15px;
      font-weight: 700;
      color: #0F1C2E;
    }

    .species-sub {
      font-size: 12.5px;
      color: #5A6E85;
      margin-top: 2px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .status-chip {
      display: flex;
      align-items: baseline;
      gap: 4px;
      padding: 5px 10px;
      border-radius: 12px;
      font-size: 12.5px;
      font-weight: 700;
    }

    .chip-big {
      font-size: 15px;
      font-weight: 800;
    }

    .chip-keep {
      background: #E8F8F0;
      color: #1F9D61;
    }

    .chip-closed {
      background: #FDE8E8;
      color: #D73333;
    }

    .chip-release, .chip-info {
      background: #F0F4F8;
      color: #5A6E85;
    }

    .disclaimer-text {
      font-size: 12px;
      color: #8C9CAE;
      line-height: 1.4;
      margin-top: 14px;
      padding: 0 4px;
    }

    .following-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .followed-card {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 12px 14px;
      background: #ffffff;
      border: 1px solid #E5EAF0;
      border-radius: 16px;
      cursor: pointer;
      position: relative;
    }

    .followed-info {
      flex: 1;
    }

    .followed-title {
      font-size: 15px;
      font-weight: 700;
      color: #0F1C2E;
    }

    .followed-sub {
      font-size: 12px;
      color: #5A6E85;
      margin-top: 1px;
    }

    .followed-status {
      font-size: 13.5px;
      font-weight: 600;
      color: #5A6E85;
      margin-top: 4px;
    }

    .followed-status.is-new {
      color: #007AFF;
    }

    .new-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #007AFF;
      margin-top: 6px;
    }

    .btn-unfollow {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: transparent;
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #8C9CAE;
      cursor: pointer;
    }

    .btn-unfollow:hover {
      background: #FDE8E8;
      color: #D73333;
    }

    .changes-timeline-card {
      margin-top: 8px;
      padding: 14px 16px;
    }

    .section-label {
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.5px;
      color: #5A6E85;
      text-transform: uppercase;
      margin-bottom: 8px;
    }

    .timeline-items {
      display: flex;
      flex-direction: column;
    }

    .timeline-row {
      display: flex;
      gap: 12px;
      padding: 10px 0;
      border-top: 1px solid #F0F4F8;
    }

    .timeline-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #C7C7CC;
      margin-top: 5px;
    }

    .timeline-dot.dot-new {
      background: #007AFF;
    }

    .timeline-dot.dot-upcoming {
      background: #E68A00;
    }

    .timeline-body {
      flex: 1;
    }

    .timeline-time {
      font-size: 12.5px;
      font-weight: 600;
      color: #5A6E85;
      margin-bottom: 2px;
    }

    .timeline-text {
      font-size: 13.5px;
      color: #0F1C2E;
      line-height: 1.35;
    }

    .timeline-fish {
      font-weight: 700;
    }

    .no-follows-empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 48px 16px;
      text-align: center;
      color: #5A6E85;
    }

    .empty-icon {
      font-size: 48px;
      color: #A0B2C6;
      margin-bottom: 12px;
    }

    .empty-title {
      font-size: 16px;
      font-weight: 700;
      color: #0F1C2E;
      margin-bottom: 6px;
    }

    .empty-desc {
      font-size: 13.5px;
      line-height: 1.4;
      max-width: 320px;
    }

    .ask-bar-container {
      padding: 8px 16px 12px;
      background: #ffffff;
      border-top: 1px solid #EAEFF5;
    }

    .ask-form {
      display: flex;
      align-items: center;
      background: #F0F4F8;
      border-radius: 22px;
      padding: 4px 6px 4px 14px;
    }

    .ask-input {
      flex: 1;
      background: transparent;
      border: none;
      outline: none;
      font-size: 15px;
      color: #0F1C2E;
    }

    .ask-submit-btn {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: #007AFF;
      border: none;
      color: #ffffff;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }

    .ask-submit-btn:disabled {
      background: #B0C8E8;
      cursor: not-allowed;
    }

    .center-loading {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 32px;
    }

    .spinner-view {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      color: #5A6E85;
    }

    .spin {
      animation: spin 1s linear infinite;
      font-size: 32px;
    }

    .card-box {
      background: #ffffff;
      border: 1px solid #E5EAF0;
      border-radius: 18px;
      overflow: hidden;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    }
  `],
})
export class KeepItSheetComponent implements OnInit, OnDestroy {
  @Input() initialSpeciesId?: string;
  @Input() initialSector?: Sector;
  @Input() initialWaters?: Waters;
  @Input() initialFollowing = false;

  @Output() close = new EventEmitter<void>();

  api = inject(RegsApiService);
  followsService = inject(RegsFollowService);
  choice = inject(RegsChoiceService);
  cdr = inject(ChangeDetectorRef);

  confirmText = regsConfirmText;

  index: RegsIndex | null = null;
  error: string | null = null;
  isFollowingTab = false;

  askText = '';
  askReply: string | null = null;

  navStack: NavEntry[] = [];

  ngOnInit(): void {
    this.isFollowingTab = this.initialFollowing;

    if (this.initialSector) this.choice.sector.set(this.initialSector);
    if (this.initialWaters) {
      this.choice.waters.set(this.initialWaters);
      this.choice.chosenByHand.set(true);
    } else {
      this.choice.locate();
    }

    // Initialize nav stack with list
    this.navStack = [{ view: 'list' }];
    if (this.initialSpeciesId) {
      this.navStack.push({
        view: 'answer',
        speciesId: this.initialSpeciesId,
        sector: this.initialSector || this.choice.sector(),
        waters: this.initialWaters || this.choice.waters(),
      });
    }

    this.loadIndex();
  }

  ngOnDestroy(): void {}

  get currentEntry(): NavEntry {
    return this.navStack[this.navStack.length - 1];
  }

  get headerSubtitle(): string {
    if (this.choice.locating()) return 'Finding your waters…';
    if (this.choice.canShowRules()) return `${this.choice.waters().label} · today`;
    const h = this.choice.here();
    if (!h) return 'Waters not checked · pick a state';
    return `${h.name || 'Covered waters'} · no rules yet`;
  }

  get followsCount(): number {
    return this.followsService.ids.length;
  }

  get followingTabLabel(): string {
    return this.followsCount > 0 ? `Following · ${this.followsCount}` : 'Following';
  }

  get skipperGreeting(): string {
    if (this.askReply) return this.askReply;
    if (this.isFollowingTab) {
      return this.followsCount === 0
        ? 'Tap Follow on any fish and I’ll tell you when its rules, season or quota change.'
        : '';
    }
    if (this.choice.showingElsewhere()) {
      return `Here’s what you can keep in ${this.choice.waters().label} today. You’re in ${this.choice.hereWaters()?.label}, so these aren’t the rules where you are.`;
    }
    if (this.choice.canShowRules()) {
      return 'Here’s what you can keep today. Tap a fish, or ask me something like “28 inch fluke?”';
    }
    return 'Choose a state below and I’ll show you what you can keep there.';
  }

  get whereNoteText(): string {
    const here = this.choice.here();
    if (!here) return '';
    const who = this.choice.hereFromBoat() ? 'Your boat is' : 'You’re';
    const off = here.offshoreNm;
    const nm = off ? (off < 10 ? off.toFixed(1) : Math.round(off).toString()) : null;
    const near = here.nearestState ? ` off ${regsStateName(here.nearestState)}` : '';
    const w = this.choice.hereWaters();

    let where = '';
    if (w && !w.isFederal && here.onLand) {
      where = `${who} on land in ${regsStateName(here.state || w.key)}, by ${w.label}.`;
    } else if (w && !w.isFederal && here.inBay) {
      where = `${who} in ${w.label} (${here.waterName}).`;
    } else if (w && !w.isFederal) {
      where = nm ? `${who} in ${w.label}, about ${nm} NM off the coast.` : `${who} in ${w.label}.`;
    } else if (w) {
      where = nm ? `${who} in ${w.lowerLabel}, about ${nm} NM${near}.` : `${who} in ${w.lowerLabel}${near}.`;
    } else {
      where = `${who} outside the waters I cover.`;
    }

    const showing = this.choice.showingElsewhere()
      ? ` Showing ${this.choice.waters().label} rules, which you picked.`
      : ` Showing ${this.choice.waters().label} rules.`;

    const boundary = here.nearBoundary && !this.choice.showingElsewhere()
      ? ' You’re close to a boundary, so confirm which waters you’re in.'
      : '';

    return `${where}${boundary}${showing}`.trim();
  }

  get unknownNoteText(): string {
    const why = this.choice.problem() === 'noFix'
      ? 'I couldn’t get a position from your boat or phone just now, so I don’t know which waters you’re in.'
      : 'I couldn’t check which waters you’re in. There’s no connection right now.';
    const next = this.choice.chosenByHand()
      ? ` Showing ${this.choice.waters().label} rules.`
      : ' Pick the state whose rules you want.';
    return `${why}${next}`;
  }

  get pairedWaters(): Waters | null {
    return regsPair(this.choice.waters(), this.choice.here());
  }

  sectorLabel(s: Sector): string {
    return s === 'commercial' ? 'Commercial' : 'Recreational';
  }

  async loadIndex(): Promise<void> {
    this.error = null;
    try {
      const idx = await this.api.index();
      this.index = idx;
      this.choice.useIndex(idx);
      this.cdr.markForCheck();
    } catch (_) {
      this.error = 'Couldn’t load regulations index.';
      this.cdr.markForCheck();
    }
  }

  getSpeciesInGroup(group: string): SpeciesSummary[] {
    if (!this.index) return [];
    return this.index.species.filter(
      (s) => s.group === group && s.hasRulesFor(this.choice.sector(), this.choice.waters())
    );
  }

  getSpeciesSub(sp: SpeciesSummary): string {
    const q = sp.quick(this.choice.sector(), this.choice.waters());
    const status = sp.status(this.choice.sector(), this.choice.waters());
    const noRet = sp.noRetention(this.choice.sector(), this.choice.waters());
    const size = sizeText(q);

    if (noRet) return 'No retention today';
    if (status === 'closed') {
      const next = sp.next(this.choice.sector(), this.choice.waters());
      return next ? `Closed until ${mdLabel(next.from)}` : 'Closed today';
    }
    const parts = [size, q.bag].filter((p) => p && p.trim().length > 0);
    return parts.join(' · ');
  }

  getChipClass(sp: SpeciesSummary): string {
    const status = sp.status(this.choice.sector(), this.choice.waters());
    const noRet = sp.noRetention(this.choice.sector(), this.choice.waters());
    if (status === 'open' && !noRet) return 'chip-keep';
    if (status === 'closed' || noRet) return 'chip-closed';
    if (status === 'prohibited' || status === 'catch_and_release') return 'chip-release';
    return 'chip-info';
  }

  getChipBag(sp: SpeciesSummary): string | null {
    const status = sp.status(this.choice.sector(), this.choice.waters());
    const noRet = sp.noRetention(this.choice.sector(), this.choice.waters());
    if (status !== 'open' || noRet) return null;
    const q = sp.quick(this.choice.sector(), this.choice.waters());
    const [big] = splitBag(q.bag);
    return big ?? null;
  }

  getChipLabel(sp: SpeciesSummary): string {
    const status = sp.status(this.choice.sector(), this.choice.waters());
    const noRet = sp.noRetention(this.choice.sector(), this.choice.waters());
    if (noRet) return 'No retention';
    if (status === 'open') {
      const q = sp.quick(this.choice.sector(), this.choice.waters());
      const [big] = splitBag(q.bag);
      return big ? 'Keep' : 'Open';
    }
    if (status === 'closed') return 'Closed';
    if (status === 'prohibited') return 'Release';
    return 'Check';
  }

  get followedItems(): Array<{
    sp: SpeciesSummary;
    sector: Sector;
    statusText: string;
    isNew: boolean;
  }> {
    if (!this.index) return [];
    const byId = new Map(this.index.species.map((s) => [s.id, s]));
    const items: Array<{ sp: SpeciesSummary; sector: Sector; statusText: string; isNew: boolean }> = [];

    for (const fid of this.followsService.ids) {
      const dot = fid.lastIndexOf('.');
      const spId = fid.substring(0, dot);
      const sector: Sector = fid.substring(dot + 1) === 'commercial' ? 'commercial' : 'recreational';
      const sp = byId.get(spId);
      if (!sp) continue;

      const latest = this.followsService.recent.find((e: ChangeEvent) => e.follow === fid);
      const isNew = latest ? this.followsService.isNew(latest) : false;
      const next = sp.next(sector, this.choice.waters());

      let statusText = 'No changes since you followed';
      if (latest) {
        statusText = `${latest.title} (${this.formatTimeAgo(latest.at)})`;
      } else if (next) {
        statusText = `Next change ${mdLabel(next.from)}: ${next.label || 'new limits'}`;
      }

      items.push({ sp, sector, statusText, isNew });
    }
    return items;
  }

  get upcomingEvents(): Array<{ when: string; fish: string; what: string }> {
    if (!this.index) return [];
    const list: Array<{ when: string; fish: string; what: string }> = [];
    const byId = new Map(this.index.species.map((s) => [s.id, s]));

    for (const fid of this.followsService.ids) {
      const dot = fid.lastIndexOf('.');
      const spId = fid.substring(0, dot);
      const sector: Sector = fid.substring(dot + 1) === 'commercial' ? 'commercial' : 'recreational';
      const sp = byId.get(spId);
      if (!sp) continue;

      const next = sp.next(sector, this.choice.waters());
      if (next) {
        list.push({
          when: `Coming ${mdLabel(next.from)}`,
          fish: sp.short,
          what: next.label ? `changes to ${next.label}` : 'new season limits',
        });
      }
    }
    return list;
  }

  formatTimeAgo(d: Date): string {
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  }

  setFollowingTab(val: boolean): void {
    this.isFollowingTab = val;
    if (!val) {
      this.followsService.markAllSeen();
    }
    this.cdr.markForCheck();
  }

  openAnswer(speciesId: string, sector?: Sector): void {
    this.navStack.push({
      view: 'answer',
      speciesId,
      sector: sector || this.choice.sector(),
      waters: this.choice.waters(),
    });
  }

  openWatersPicker(): void {
    this.navStack.push({ view: 'watersPicker' });
  }

  onWatersSelected(w: Waters): void {
    this.choice.pick(w);
    this.popStack();
  }

  pushStackAnswer(event: {
    speciesId?: string;
    sector?: Sector;
    waters?: Waters;
    question: string;
    askedInches?: number;
    intro?: string;
  }): void {
    this.navStack.push({
      view: 'answer',
      speciesId: event.speciesId || this.currentEntry.speciesId,
      sector: event.sector || this.currentEntry.sector || this.choice.sector(),
      waters: event.waters || this.currentEntry.waters || this.choice.waters(),
      question: event.question,
      askedInches: event.askedInches ?? undefined,
      intro: event.intro,
    });
  }

  pushStackId(species: Species, lookalike: Lookalike): void {
    this.navStack.push({
      view: 'idPage',
      species,
      lookalike,
    });
  }

  pushStackChanges(species: Species, sector: Sector): void {
    this.navStack.push({
      view: 'changesPage',
      species,
      sector,
    });
  }

  popStack(): void {
    if (this.navStack.length > 1) {
      this.navStack.pop();
      this.cdr.markForCheck();
    }
  }

  confirmUnfollow(event: Event, speciesId: string, sector: Sector, name: string): void {
    event.stopPropagation();
    if (confirm(`Stop following ${name}? I won’t tell you when rules, seasons or quotas change.`)) {
      this.followsService.unfollow(speciesId, sector);
    }
  }

  submitAsk(): void {
    const q = this.askText.trim();
    if (!q || !this.index) return;

    const m = parseAsk(q, this.index.species);
    if (!m) {
      this.askReply = `I don’t know that one yet. I cover ${this.index.species.length} fish. Try a name like “fluke” or “bluefin”.`;
      this.cdr.markForCheck();
      return;
    }

    this.askText = '';
    this.askReply = null;
    this.navStack.push({
      view: 'answer',
      speciesId: m.species.id,
      sector: this.choice.sector(),
      waters: this.choice.waters(),
      question: q,
      askedInches: m.inches ?? undefined,
    });
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.close.emit();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  handleEscape(event: KeyboardEvent): void {
    event.preventDefault();
    this.close.emit();
  }

  avatarFallback(event: Event): void {
    const target = event.target as HTMLElement;
    target.style.display = 'none';
  }
}
